/* eslint-disable no-redeclare */
import { SegmentType } from '@ve-pro/enums/segment'
import { TrackMode, TrackType } from '@ve-pro/enums/track-type'
import type {
  SegmentMaterial,
  SegmentCollection,
  Track,
  SegmentText,
  SegmentTransition,
  SegmentGroupCollection,
  SegmentNoGroupCollection,
  InferGroupTypeFromChild,
  _Track,
  SegmentFragment,
  SegmentSubtitleGroup,
} from '@ve-pro/types/track.type'
import { createTrackAndInsertSegment } from '@ve-pro/ve/timeline/track/track-expect-insert'
import { getTrackTypeBySegmentType } from '@ve-pro/ve/timeline/track/track-height'
import { TRACK_SUBTITLES, useTrackStore } from '.'
import { useHistoryStore } from '../history'
import { generateSegmentIdFromService, generateTrackId } from './generate-id'
import {
  defineSegmentProperties,
  hasSegmentMaterial,
  replaceMaterial,
  resolveSegment,
  resolveSegmentGroup,
  resolveSegmentText,
  resolveSegmentTransition,
} from './resolve-segment'
import type { ResolveMaterialSegmentReturnType } from './resolve-segment'
import { addSegment, isLangTrack } from './tracks-actions'
import { updateSegmentRatiosToContain } from '@ve-pro/ve/player/utils'
import { cloneDeep } from 'lodash-es'
import { patchSegment } from './segment/patch'
import { VideoEventter } from '@ve-pro/ve/utils/event-bus'
import type { SupportTransitionSegment } from './segment/segment-transition'
import {
  getCurrentTimeSegmentPositionRelative,
  getTransition,
  getTransitionOverlap,
  getTransitionVideoDurationMin,
  isTransition,
} from './segment/segment-transition'
import { showInsertError } from '@ve-pro/ve/timeline/track/track-message'
import { getSortedSegments, updateMainTrack } from './update-main-track'
import { t } from '@ve-pro/ve/lang'
import { isSegment } from './segment'
import { isConvertibleToSegmentGroup } from './segment/segment-group'
import type { AssetGroup } from '@ve-pro/api/asset-group/type'
import Vue from 'vue'
import { useSave } from '@ve-pro/ve/hotkeys/shortcuts/save/save-action'
import { ProjectMode } from '@ve-pro/enums/project'
import type { RelativePosition } from '@ve-pro/ve/timeline/track/track-insert-position'
import { add } from '@com/utils/math'
import { setActiveSegment } from './toggle-active-segment'
import { apiAddSegment } from '@ve-pro/api/project'
import { useProjectStore } from '../project'
import { AssetType } from '@ve-pro/enums/asset'
import { ProjectSegmentModel } from '@ve-pro/api/project/type'
import { veproLogs } from '@com/utils/report-logs/ve-pro'
import {
  isSegmentFragment,
  isSegmentFragmentChild,
} from './segment/segment-fragment'
import { resolveFragmentTracksSegmentDuration } from './add-segment-fragment'
import { isSegmentSubtitleGroup } from './segment/segment-subtitle'
import { isSupportRatios } from './segment/segment-ratio'

type SegmentTransitionSegments = {
  before: SupportTransitionSegment
  behind: SupportTransitionSegment
}

// 添加转场验证器，若验证成功，返回
export function getSegmentTransitionVideo(): null | SegmentTransitionSegments {
  const trackStore = useTrackStore()

  function reject(msg: string) {
    showInsertError(msg)
    return null
  }

  if (trackStore.mainTack && trackStore.mainTack.segments.length < 2) {
    return reject(t('track.transition.add.error1'))
  }

  let _target: ReturnType<typeof getCurrentTimeSegmentPositionRelative> =
    getCurrentTimeSegmentPositionRelative()

  if (!_target) {
    const sortedSegments = getSortedSegments({
      track: trackStore.mainTack ?? undefined,
    })
    const last = sortedSegments[sortedSegments.length - 1]
    if (last) {
      _target = {
        segment: last,
        position: 'left',
      }
    }
  }

  if (_target) {
    const target = _target
    // 获取排序后的元件
    const segments = getSortedSegments()
    const targetIndex = segments.findIndex(
      (seg) => seg.id === target.segment.id,
    )
    let before: SupportTransitionSegment | null = null
    let behind: SupportTransitionSegment | null = null

    if (target.position === 'left') {
      const prev = segments[targetIndex - 1]
      before = prev ?? null
      behind = target.segment

      // 兼容第一个视频
      if (!before) {
        before = target.segment
        behind = segments[targetIndex + 1] ?? null
      }
    } else {
      const next = segments[targetIndex + 1]
      before = target.segment
      behind = next ?? null

      // 兼容最后一个视频
      if (!behind) {
        before = segments[targetIndex - 1] ?? null
        behind = target.segment
      }
    }

    if (!before || !behind) {
      return reject(t('track.transition.add.error2'))
    }

    if (isSegmentFragment(before) || isSegmentFragment(behind)) {
      return reject(t('track.transition.add.noSupportFragment'))
    }

    const min = getTransitionVideoDurationMin()

    if (before.duration < min || behind.duration < min) {
      return reject(t('track.transition.add.error3'))
    }

    return { before, behind }
  }

  return reject(t('track.transition.add.error2'))
}

/**
 * 添加转场到主轨道上
 * @param material
 * @returns
 */
export async function addSegmentTransition(
  material: SegmentMaterial,
): Promise<SegmentTransition> {
  const history = useHistoryStore()
  const trackStore = useTrackStore()

  const value = getSegmentTransitionVideo()
  if (value) {
    const { before, behind } = value
    const overlap = getTransitionOverlap(material)
    // 如果相同位置存在，这替换转场
    const { behind: behindTransition } = getTransition(before)

    if (behindTransition) {
      // 资源不相同才需要替换
      if (behindTransition.asset_id !== material.asset_id) {
        // 需要更新转场元件的id
        const newTransitionId = await generateSegmentIdFromService(
          behindTransition,
        )
        behindTransition.id = newTransitionId
        before.behind_transition_id = newTransitionId
        behind.before_transition_id = newTransitionId

        replaceMaterial(behindTransition, material)
        if (behindTransition.overlap !== overlap) {
          // 根据素材给定折叠
          behindTransition.overlap = overlap
          updateMainTrack()
        }
      }

      setActiveSegment(behindTransition)
      history.push()
      return behindTransition
    }

    const transition = resolveSegmentTransition(material, before, behind)

    defineSegmentProperties(transition, {
      material_duration: transition.material?.duration ?? transition.duration,
      trim_start_time: transition.trim_start_time,
      duration: transition.duration,
    })
    const result = await addSegmentToTrack(transition, {
      stage: ProjectMode.Default,
    })
    history.push()

    setActiveSegment(result)
    return result
  }

  return Promise.reject()
}

type ResolvableSegments =
  | NonNullable<ResolveMaterialSegmentReturnType>
  | SegmentGroupCollection

export async function addSegmentGroup<T extends SegmentNoGroupCollection>(
  children: T[],
  assetGroupId: AssetGroup['id'],
  options?: {
    logAction?: ReturnType<typeof veproLogs.createAction>
    segmentGroups?: Record<SegmentCollection['id'], SegmentCollection[]>
    tracks?: Track[]
    autoSave?: boolean
  },
): Promise<InferGroupTypeFromChild<SegmentNoGroupCollection>> {
  const trackStore = useTrackStore()
  const autoSave = options?.autoSave ?? true

  const segmentGroups = options?.segmentGroups ?? trackStore.segmentGroups
  const tracks = options?.tracks ?? trackStore.tracks
  const segments = tracks.reduce<SegmentCollection[]>((result, track) => {
    return [...result, ...track.segments]
  }, [])

  const action = options?.logAction

  // 获取最后一个
  const last = children[children.length - 1]!
  if (!isConvertibleToSegmentGroup(last)) {
    return Promise.reject()
  }

  const segment = resolveSegmentGroup(last)
  segment.asset_group_id = assetGroupId
  const group = segment

  const task = generateSegmentGroup(segment)
  action?.apiLog(task)
  const segmentGroupResult = await resolveSegmentGroupResponse(task)

  const segmentId = segmentGroupResult.value.id
  const isReplace = segments.some((item) => item.id === last.id)
  let result: SegmentGroupCollection

  const addChildren = (options: Pick<SegmentCollection, 'id' | 'rate'>) => {
    Vue.set(
      segmentGroups,
      segmentId,
      children.map((item) => {
        const target = segmentGroupResult.children.find((t) => {
          if (item.type === SegmentType.Text) {
            return t.res_id === item.res_id
          }
          return t.asset_id === item.asset_id
        })

        const segment = {
          ...item,
          rate: options.rate,
          start_time: 0, // 所有的子元件都是从0开始
          pid: options.id,
          res_id: target?.res_id,
          id: target?.id,
        }

        const material_duration =
          segment.rate === 1
            ? segment.material?.duration ?? segment.duration
            : segment.source_rate.material_duration

        const trim_start_time =
          segment.rate === 1
            ? segment.trim_start_time
            : segment.source_rate.trim_start_time

        const duration =
          segment.rate === 1 ? segment.duration : segment.source_rate.duration
        defineSegmentProperties(segment, {
          material_duration,
          trim_start_time,
          duration,
        })

        // 组如果有缩放，子元件就不需要，不然有叠加
        if (isSupportRatios(segment)) {
          Object.values(segment.ratios ?? {}).forEach((ratio) => {
            ratio.scale_x = 100
            ratio.scale_y = 100
          })
        }

        updateSegmentRatiosToContain(segment, group)
        return segment
      }) as SegmentNoGroupCollection[],
    )
  }

  if (isReplace) {
    segment.id = segmentId

    if (segment.type === SegmentType.Video) {
      const { before, behind } = getTransition(segment)
      if (before) {
        before.behind = segment
      }
      if (behind) {
        behind.before = segment
      }
    }

    const targetTrack = tracks.find((t) => t.id === segment.track_id)
    const targetSegmentIndex =
      targetTrack?.segments.findIndex((s) => s.id === last.id) ?? -1

    if (targetSegmentIndex > -1) {
      targetTrack?.segments.splice(targetSegmentIndex, 1, segment as any)
    }

    // 普通元件转为组的时候，返回的子资源res_id 要复制给原有的普通元件，
    // 这样下面的逻辑就能关联上
    last.res_id = segmentGroupResult.children[0]?.res_id ?? 0

    result = segment
    addChildren(result)
  } else {
    updateSegmentRatiosToContain(group)
    // 提前将子元件的添加到状态中，主要是下面的异步操作传了id，可以保证一定成功
    addChildren({ id: segmentId, rate: segment.rate })
    result = await addSegmentbyCurrentTime(segment, {
      id: segmentId,
      stage: ProjectMode.Default, // 组仅限于「创意制作」
    })
  }

  if (autoSave) {
    useSave()
  }

  return result
}

function generateSegmentGroup(
  segment: SegmentGroupCollection,
): ReturnType<typeof apiAddSegment> {
  const projectStore = useProjectStore()
  return apiAddSegment({
    stage: projectStore.mode,
    asset_group_id: segment.asset_group_id,
    project_id: projectStore.id,
    type:
      segment.type === SegmentType.Text
        ? AssetType.Text
        : segment.material.type,
  })
}

async function resolveSegmentGroupResponse(
  _res: ReturnType<typeof generateSegmentGroup>,
): Promise<{
  value: ProjectSegmentModel
  children: ProjectSegmentModel[]
}> {
  const res = await _res
  const segments = res.data.data.segments
  const value = segments.find((s) => s.pid === 0)!

  const children = segments.filter((s) => s.pid > 0)

  return {
    value,
    children,
  }
}

interface AddSegmentOptions {
  // 元件id
  id?: SegmentCollection['id']
  // 元件stage
  stage?: ProjectMode
  // 所属片段元件id
  fragment_segment_id?: SegmentFragment['id']
  shouldPushHistory?: boolean
}

/**
 * 根据当前的时间插入（）
 * @param type
 *
 * 备注：当前这个组件异步是为了创建元件的id，这个id是在服务端生成的，如果不异步，那么在插入的时候，id是不确定的
 * 但是调用放穿了id后，就不需要异步了，因为id是确定的
 *
 * addSegmentGroup 函数利用了上面这个特性（传了id），所以不需要异步，可保证它一定成功
 *
 * ！！！如果这个函数新增了其他异步行为，（addSegmentGroup）需要注意这个问题
 */
export async function addSegmentbyCurrentTime<T extends ResolvableSegments>(
  segment: T,
  options?: AddSegmentOptions,
): Promise<T>
export async function addSegmentbyCurrentTime(
  type: SegmentType.Text,
  options?: AddSegmentOptions,
): Promise<SegmentText>
export async function addSegmentbyCurrentTime(
  material: SegmentMaterial,
  options?: AddSegmentOptions,
): Promise<ResolveMaterialSegmentReturnType>
export async function addSegmentbyCurrentTime(
  materialOrSegmentTextType: SegmentMaterial | SegmentType.Text,
  options?: AddSegmentOptions,
): Promise<ResolveMaterialSegmentReturnType | SegmentText>
export async function addSegmentbyCurrentTime(
  materialOrSegmentTextType:
    | SegmentMaterial
    | SegmentType.Text
    | ResolvableSegments,
  options?: AddSegmentOptions,
): Promise<ResolveMaterialSegmentReturnType | SegmentText> {
  const history = useHistoryStore()
  const trackStore = useTrackStore()

  const { shouldPushHistory = true } = options ?? {}

  // 保证数据的不污染
  materialOrSegmentTextType = cloneDeep(materialOrSegmentTextType)

  // eslint-disable-next-line no-undef
  let segment: Awaited<ReturnType<typeof addSegmentbyCurrentTime>>

  if (materialOrSegmentTextType === SegmentType.Text) {
    segment = resolveSegmentText(
      undefined,
      options?.stage === ProjectMode.Subtitle,
    )
  } else if (isSegment(materialOrSegmentTextType)) {
    // 不复制，保持传进来的引用关系
    segment = materialOrSegmentTextType
  } else {
    segment = resolveSegment(materialOrSegmentTextType)
  }

  if (!segment) {
    return Promise.reject()
  }
  segment.start_time = Math.floor(trackStore.currentTime)

  defineSegmentProperties(segment, {
    material_duration: hasSegmentMaterial(segment)
      ? segment.material?.duration ?? segment.duration
      : segment.duration,
    trim_start_time: segment.trim_start_time,
    duration: segment.duration,
  })

  updateSegmentRatiosToContain(segment)

  const result = await addSegmentToTrack(segment, options)

  if (shouldPushHistory) {
    history.push()
  }

  return result
}

/**
 * 将一个完善的元件添加轨道区里面（需要保证整体数据是完善的）
 * @param segment
 * @returns
 */
export async function addSegmentToTrack<
  T extends Exclude<SegmentCollection, SegmentFragment | SegmentSubtitleGroup>,
>(
  segment: T, // 不可响应的对象
  options?: AddSegmentOptions,
): Promise<T> {
  const trackStore = useTrackStore()
  const fragment_segment_id:
    | SegmentCollection['fragment_segment_id']
    | undefined =
    options?.fragment_segment_id ?? trackStore.activeSegmentFragment?.id
  const id = options?.id ?? (await generateSegmentIdFromService(segment))

  patchSegment(segment, {
    id: id,
    stage: options?.stage ?? segment.stage,
    fragment_segment_id,
  })
  resolveFragmentTracksSegmentDuration(segment)

  if (isTransition(segment)) {
    segment.before.behind_transition_id = segment.id
    segment.behind.before_transition_id = segment.id
  }

  let insertResult = true
  const trackType = getTrackTypeBySegmentType(segment.type)
  if (trackType === TrackType.Audio) {
    insertResult = insertToAudioTrack(segment)
  } else if (trackType === TrackType.Video) {
    insertToVideoTrack(segment)
  } else {
    insertResult = insertToOtherTrack(segment)
  }

  if (!insertResult) {
    return Promise.reject()
  }

  setActiveSegment(segment)

  // 通知全局有新元件添加到轨道中了
  VideoEventter.emit('addSegment', segment)
  return segment
}

export function createTrack<T extends TrackType>(type: T): _Track<T> {
  const track: _Track<T> = {
    id: generateTrackId(),
    segments: [],
    type: type,
    mute: false,
    is_hide: false,
    is_main: false,
  }
  return track
}

function createTrackAndInsertSegmentAdapter(
  segment: SegmentCollection,
  position: RelativePosition,
  relativeTrack?: Track,
): boolean {
  const trackStore = useTrackStore()
  const trackType = getTrackTypeBySegmentType(segment.type)
  // 字幕&配音
  if (
    segment.stage === ProjectMode.Subtitle &&
    trackStore.tracks.length === 0
  ) {
    const track = createTrack(trackType) as Track
    addSegment(segment, track)

    trackStore.tracks = [track]
    // 字幕模式，需要展示主轨道
    trackStore.createDefaultModeTrackMainIfNotExist()
    return true
  }

  if (!relativeTrack) {
    if (isSegmentFragmentChild(segment)) {
      relativeTrack = trackStore.createFragmentMainTrackIfNotExist(
        segment.fragment_segment_id,
      )
    } else {
      relativeTrack = trackStore.createDefaultModeTrackMainIfNotExist()
    }
  }

  return createTrackAndInsertSegment(relativeTrack, position, segment)
}

export function createAndAddDefaultModeMainTrack(): Track<TrackType.Video> {
  const trackStore = useTrackStore()
  const mainTack = createTrack(TrackType.Video)
  mainTack.is_main = true
  const defaultExcludeFragmentMainTrack =
    trackStore.defaultExcludeFragmentMainTrack

  const appendTo = (target: Track): void => {
    const index = trackStore.tracks.findIndex((track) => track.id === target.id)

    trackStore.tracks.splice(index, 0, mainTack)
  }

  if (defaultExcludeFragmentMainTrack) {
    defaultExcludeFragmentMainTrack.is_main = false
    appendTo(defaultExcludeFragmentMainTrack)
  } else {
    const otherTracks = trackStore.tracks.filter(
      (t) => t.type !== TrackType.Audio,
    )

    const first = otherTracks[0]
    if (first) {
      appendTo(first)
    } else {
      trackStore.tracks.push(mainTack)
    }
  }

  return mainTack
}

/**
 * 插入音频元件
 * 需求：从上往下搜索是否有合适的音频轨道，若没有，则选择第一个轨道
 * 第一个轨道的类型：
 * 情况1: 存在音频轨道时候：最底下的音频轨道
 * 情况2: 不存在音频轨道时候：视频主轨道
 * @param segment 新的音频元件
 * @returns boolean 是否插入成功
 */
export function insertToAudioTrack(segment: SegmentCollection): boolean {
  const trackStore = useTrackStore()

  const stage = segment.stage
  const tracks = isSegmentFragmentChild(segment)
    ? trackStore.getTracksByFragmentSegmentId(segment.fragment_segment_id)
    : trackStore.getCurrentTracksByMode(stage)
  const targetTrack = getNoConflictTrack([...tracks].reverse(), segment)

  if (targetTrack) {
    addSegment(segment, targetTrack)
  } else {
    return createTrackAndInsertSegmentAdapter(segment, 'bottom', tracks[0])
  }

  return true
}

/**
 * 插入视频元件
 * 添加视频只会进入主轨道
 * 兼容转场、插入位置的判断
 * @param segment 新的视频元件
 */
function insertToVideoTrack(segment: SegmentCollection) {
  const trackStore = useTrackStore()
  const target = getCurrentTimeSegmentPositionRelative()

  if (!isTransition(segment) && target) {
    const targetSegment = target.segment
    const { before, behind } = getTransition(targetSegment)
    if (target.position === 'left') {
      segment.start_time = targetSegment.start_time - 1
      if (before && segment.type === SegmentType.Video) {
        before.behind = segment
        segment.before_transition_id = targetSegment.before_transition_id
        targetSegment.before_transition_id = null
      }
    } else {
      segment.start_time = targetSegment.start_time + 1
      if (behind && segment.type === SegmentType.Video) {
        behind.before = segment
        segment.behind_transition_id = targetSegment.behind_transition_id
        targetSegment.behind_transition_id = null
      }
    }
  }

  if (trackStore.mode === TrackMode.Tradition) {
    addSegment(segment, trackStore.createDefaultModeTrackMainIfNotExist())
    updateMainTrack()
  } else if (isSegmentFragmentChild(segment)) {
    addSegment(
      segment,
      trackStore.createFragmentMainTrackIfNotExist(segment.fragment_segment_id),
    )
    updateMainTrack()
  } else if (isTransition(segment)) {
    // 片段模式下 默认主轨道的
    addSegment(segment, trackStore.createDefaultModeTrackMainIfNotExist())
    updateMainTrack()
  } else {
    // 片段模式下，适配视频元件不能插入到主轨道
    const mainTrack = trackStore.createDefaultModeTrackMainIfNotExist()
    const tracks = trackStore.defaultExcludeFragmentTracks
      .filter((t) => t.type === TrackType.Video)
      .filter((t) => t.id !== mainTrack.id)
    const targetTrack = getNoConflictTrack(tracks, segment)

    if (targetTrack) {
      addSegment(segment, targetTrack)
    } else {
      const track = [...tracks].reverse()[0] ?? mainTrack
      createTrackAndInsertSegmentAdapter(segment, 'top', track)
    }
  }
}

/**
 * 插入轨道
 * @param segment 新的音频元件
 * @returns boolean 是否插入成功
 */
export function insertToOtherTrack(segment: SegmentCollection): boolean {
  const trackStore = useTrackStore()
  const trackType = getTrackTypeBySegmentType(segment.type)

  const stage = segment.stage
  const tracks = isSegmentFragmentChild(segment)
    ? trackStore.getTracksByFragmentSegmentId(segment.fragment_segment_id)
    : trackStore
        .getCurrentTracksByMode(stage)
        .filter((t) => t.id !== TRACK_SUBTITLES)

  const targetTrack = getNoConflictTrack(tracks, segment)

  if (targetTrack) {
    addSegment(segment, targetTrack)
  } else {
    const arr = [...tracks]
      .reverse()
      .filter((t) => !t.segments.some((s) => isSegmentSubtitleGroup(s)))
    const track = arr.find((t) => t.type === trackType) ?? arr[0]

    return createTrackAndInsertSegmentAdapter(segment, 'top', track)
  }

  return true
}

/**
 * 将裂变元件插入轨道
 * @param segment
 * @returns { void }
 */
export function insertToLangTrack(segment: SegmentCollection): void {
  const trackStore = useTrackStore()
  const trackType = getTrackTypeBySegmentType(segment.type)

  segment.fragment_segment_id = 0

  // 仅限于
  // 1.放到现有裂变元件的轨道
  // 2.放到同语言的轨道
  const LangTracks = trackStore.tracks
    .filter((t) => isLangTrack(t))
    .filter((t) => t.segments.some((s) => s.lang === segment.lang))

  const targetTrack = getNoConflictTrack(LangTracks, segment)

  if (targetTrack) {
    addSegment(segment, targetTrack)
  } else {
    const arr = [...LangTracks].reverse()
    const track = arr.find((t) => t.type === trackType) ?? arr[0]

    createTrackAndInsertSegmentAdapter(segment, 'top', track)
  }
}

export function getNoConflictTrack(
  tracks: Track[],
  segment: SegmentCollection,
): Track | undefined {
  const trackType = getTrackTypeBySegmentType(segment.type)
  return tracks.find((track) => {
    if (trackType === track.type) {
      const conflictSegment = getConflictSegment(segment, track)
      return !conflictSegment
    }
    return false
  })
}

export function getConflictSegment(
  segment: SegmentCollection,
  track?: Track,
): SegmentCollection | undefined {
  const trackStore = useTrackStore()
  track = track || trackStore.tracks.find((t) => t.id === segment.track_id)
  const segments = [...(track?.segments ?? [])].sort(
    (a, b) => a.start_time - b.start_time,
  )

  return segments.find((s) => {
    if (s.id === segment.id) {
      return false
    }

    // 由于新的segment没有轨道信息，需要保证在同一水平上进行冲突判断
    return isTimeConflict(segment, s)
  })
}

export function isTimeConflict(
  segment: SegmentCollection,
  target: SegmentCollection,
): boolean {
  const segmentStartTime = segment.start_time
  const segmentEndTime = add(segment.start_time, segment.duration)

  const targetStartTime = target.start_time
  const targetEndTime = add(target.start_time, target.duration)

  // 验证target在左边
  if (segmentStartTime >= targetEndTime) {
    return false
  }

  // 验证target在右边
  if (segmentEndTime <= targetStartTime) {
    return false
  }

  return true
}
